/**
 * =================================================================
 * SCRIPT-NAME:   Contacts.js
 * BESCHREIBUNG:   React-Komponente zur Anzeige und Verwaltung von Kontakten.
 *
 * VERSION:       v1.i8
 * DATUM:         2025-07-13 20:33:56
 *
 * ÄNDERUNGEN ZUR VORVERSION (v1.i7):
 * - Neue Spalten "IT Geb." und "Webinar Gebucht ZP" hinzugefügt.
 * - Datenverarbeitung für `it_gebucht` und `webinar_gebucht_zeitpunkt`.
 * - Gesamtspaltenanzahl auf 28 erhöht.
 * =================================================================
 */

import React, { useState, useEffect } from "react"; 
import "./Contacts.css"; 
import BackButton from "./BackButton";

// Hilfsfunktion zum Formatieren von DB-Timestamps für <input type="datetime-local">
const formatDateTimeForInput = (dateTimeString) => {
  if (!dateTimeString) return "";
  try {
    const date = new Date(dateTimeString.replace(" ", "T"));
    if (isNaN(date.getTime())) return "";
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  } catch (e) { return ""; }
};

// Hilfsfunktion zum Formatieren von Timestamps für die Anzeige (lesbar)
const formatDateTimeForDisplay = (dateTimeString) => {
  if (!dateTimeString) return "N/A";
  try {
    return new Date(dateTimeString).toLocaleString('de-DE'); 
  } catch (e) { return "Ungültig"; }
};

const funnelStages = [
  { value: "", label: "Stufe für Upload wählen..." },
  { value: "Offline_Webinar_Engagiert", label: "Webinar Engagiert (4 €)" },
  { value: "Offline_Erstgespraech_Gebucht", label: "Erstgespräch Gebucht (5 €)" },
  { value: "Offline_Erstgespraech_Gefuehrt", label: "Erstgespräch Geführt (6 €)" },
  { value: "Offline_Strategiegespraech_Gefuehrt", label: "Strategiegespräch Geführt (7 €)" },
  { value: "Offline_Coaching_Starter_Gebucht", label: "Coaching Starter Gebucht (8 €)" },
  { value: "Offline_Coaching_Kunde_C1_C5", label: "Coaching Kunde (pauschal 10 €)" },
];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [message, setMessage] = useState("");
  const token = localStorage.getItem("token");
  const [selectedStage, setSelectedStage] = useState({});
  
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timerId = setInterval(() => {
        setCurrentDateTime(new Date());
    }, 1000); 
    return () => {
        clearInterval(timerId); 
    };
  }, []); 

  const formattedDateTime = currentDateTime.toLocaleString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  useEffect(() => {
     if (!token) {
        console.error("FEHLER im useEffect: Kein Token vorhanden!");
        setMessage("❌ Authentifizierungstoken fehlt. Bitte neu anmelden.");
        setContacts([]); 
        return; 
     }

    setMessage("Lade Kontakte...");
    fetch("https://freiseinapp.de/api/get_contacts.php", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then(response => {
         if (!response.ok) {
             return response.json().then(errData => {
                 throw new Error(`HTTP Fehler! Status: ${response.status} - ${errData?.message || 'Keine Fehlermeldung'}`);
             }).catch(() => {
                 throw new Error(`HTTP Fehler! Status: ${response.status}`);
             });
         }
         return response.json();
      })
      .then(data => {
        setMessage("");
        if (data.success && Array.isArray(data.contacts)) {
          setContacts(
            data.contacts.map(contact => ({
              ...contact,
              status: contact.status || 'N/A',
              it_gebucht: Boolean(Number(contact.it_gebucht)), // NEU
              webinar_gebucht_zeitpunkt: contact.webinar_gebucht_zeitpunkt || null, // NEU
              it_besucht: Boolean(Number(contact.it_besucht)),
              eg_gebucht: Boolean(Number(contact.eg_gebucht)),
              eg_gefuehrt: Boolean(Number(contact.eg_gefuehrt)),
              sg_gebucht: Boolean(Number(contact.sg_gebucht)),
              sg_gefuehrt: Boolean(Number(contact.sg_gefuehrt)),
              coaching_starter_gebucht: Boolean(Number(contact.coaching_starter_gebucht)),
              coaching_c0: Boolean(Number(contact.coaching_c0)),
              coaching_c1: Boolean(Number(contact.coaching_c1)),
              coaching_c2: Boolean(Number(contact.coaching_c2)),
              coaching_c3: Boolean(Number(contact.coaching_c3)),
              coaching_c4: Boolean(Number(contact.coaching_c4)),
              coaching_c5: Boolean(Number(contact.coaching_c5)),
              webinar_engagiert_zeitpunkt: contact.webinar_engagiert_zeitpunkt || null,
              eg_gebucht_zeitpunkt: contact.eg_gebucht_zeitpunkt || null,
              eg_gefuehrt_zeitpunkt: contact.eg_gefuehrt_zeitpunkt || null,
              sg_gefuehrt_zeitpunkt: contact.sg_gefuehrt_zeitpunkt || null,
              coaching_starter_gebucht_zeitpunkt: contact.coaching_starter_gebucht_zeitpunkt || null,
              coaching_kunde_zeitpunkt: contact.coaching_kunde_zeitpunkt || null,
              gesendet_zeitpunkt: contact.gesendet_zeitpunkt || null,
              gclid: contact.gclid || null
            }))
          );
        } else {
           setMessage(data.message || "❌ Fehler beim Abrufen der Kontakte (API).");
           console.error("API meldet Fehler:", data);
        }
      })
      .catch(error => {
         console.error("❌ Fehler beim Laden der Kontakte (Fetch Catch):", error);
         setMessage(`❌ Fehler beim Abrufen der Kontakte: ${error.message}`);
         setContacts([]); 
      });
  }, [token]); 

  const handleCheckboxChange = (id, field, currentValue) => {
    const newValue = !currentValue;
    fetch("https://freiseinapp.de/api/update_contact.php", {
      method: "POST", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ id, field, value: newValue ? "1" : "0" }),
    })
    .then(response => response.json()).then(data => { if (data.success) {
        setContacts(prev => prev.map(c => c.id === id ? { ...c, [field]: newValue } : c)); } else { console.error("Update Checkbox Fehler:", data.message); setMessage(`❌ Update ${field} fehlgeschlagen.`);}
    }).catch(error => { console.error("Update Checkbox Netzwerkfehler:", error); setMessage(`❌ Netzwerkfehler Update ${field}.`);});
 };

 const handleNameChange = (id, newValue) => {
     fetch("https://freiseinapp.de/api/update_contact.php", {
         method: "POST", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ id, field: "nachname", value: newValue }),
     })
     .then(response => response.json()).then(data => { if (data.success) {
         setContacts(prev => prev.map(c => c.id === id ? { ...c, nachname: newValue } : c)); } else { console.error("Update Name Fehler:", data.message); setMessage(`❌ Update Name fehlgeschlagen.`);}
     }).catch(error => { console.error("Update Name Netzwerkfehler:", error); setMessage(`❌ Netzwerkfehler Update Name.`); });
 };

 const handleTimestampChange = (id, field, newValue) => {
   setContacts(prevContacts => prevContacts.map(contact => contact.id === id ? { ...contact, [field]: newValue } : contact ));
   fetch("https://freiseinapp.de/api/update_contact.php", {
     method: "POST", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ id, field, value: newValue || null }),
   })
   .then(response => response.json()).then(data => { if (!data.success) {
       console.error("⚠️ Fehler beim Speichern des Zeitstempels:", data.message); setMessage(`❌ Fehler beim Speichern: ${field} - ${data.message}`); } else { setMessage(`✅ ${field} gespeichert.`); }
   }).catch(error => { console.error("❌ Fehler beim Speichern des Zeitstempels:", error); setMessage(`❌ Netzwerkfehler beim Speichern: ${field}`); });
 };

 const handleStageSelectChange = (contactId, stageValue) => {
   console.log(`handleStageSelectChange - START - contactId: ${contactId}, stageValue: ${stageValue}`); 
   console.log(`handleStageSelectChange - State VOR Update für ID ${contactId}:`, selectedStage[contactId]); 
   setSelectedStage(prev => {
       console.log(`handleStageSelectChange - In setSelectedStage für ID ${contactId}. Neuer Wert wird: ${stageValue}`); 
       const newState = {
         ...prev,
         [contactId]: stageValue
       };
       console.log(`handleStageSelectChange - Komplettes newState Objekt nach Update für ID ${contactId}:`, newState); 
       return newState;
   });
    console.log(`handleStageSelectChange - ENDE - nach Aufruf von setSelectedStage`); 
 };

 const handleGadsUpload = (contact) => {
   const stageIdentifier = selectedStage[contact.id]; 
   if (!stageIdentifier) { setMessage(`⚠️ Bitte zuerst eine Upload-Stufe für ${contact.email || contact.id} auswählen.`); return; }
   if (!contact.gclid) { setMessage(`❌ Keine GCLID für ${contact.email || contact.id} gefunden. Upload nicht möglich.`); console.error("Keine GCLID für Kontakt:", contact); return; }
   if (!token) { setMessage(`❌ Kein Token für Upload vorhanden. Bitte neu anmelden.`); return; } 

   setMessage(`Sende Upload-Anfrage für ${stageIdentifier} bei ${contact.email || contact.id} an Backend...`);
   
   const uploadPayload = { 
       gclid: contact.gclid, 
       stage: stageIdentifier, 
       contactId: contact.id 
   };

   console.log("📤 Sende Upload-Anfrage an Backend:", uploadPayload);
   fetch("https://freiseinapp.de/api/send_conversion_to_gads.php", {
       method: "POST",
       headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
       body: JSON.stringify(uploadPayload),
     })
   .then(response => response.json()).then(data => { 
       console.log("📊 GADS Upload Response from Backend:", data); 
       if (data.success) {
           setMessage(`✅ ${stageIdentifier} für ${contact.email || contact.id} erfolgreich ans Backend übergeben.`); 
           const nowTimestamp = new Date().toISOString();
           setContacts(prevContacts => prevContacts.map(c => 
               c.id === contact.id ? { ...c, gesendet_zeitpunkt: nowTimestamp } : c 
           ));
       } else { 
           setMessage(`❌ Fehler beim GADS Upload für ${contact.email || contact.id}: ${data.message || data.error || 'Unbekannter Fehler'}`); 
           console.error("⚠️ Fehler vom Backend:", data); 
       }
   }).catch(error => { 
       setMessage(`❌ Netzwerkfehler beim GADS Upload für ${contact.email || contact.id}.`); 
       console.error("❌ Fehler beim Senden an Backend:", error); 
   });
 };

  const numberOfColumns = 28; 

  return (
    <div className="contacts-container">
      
      <div className="contacts-page-header">
        <p className="contacts-current-datetime">
            Aktuell: {formattedDateTime}
        </p>
        <h2>📋 Kontakte verwalten & GADS Upload</h2>
      </div>
      
      {message && <p className={message.startsWith('❌') || message.startsWith('⚠️') ? "contacts-error" : "contacts-success"}>{message}</p>}

      <div className="contacts-table-container">
        <table className="contacts-table">
           <thead>
             <tr>
               <th>Email</th><th>Status</th><th>Vorname</th><th>Nachname</th><th>Erstellt am</th><th>Gesendet am</th>
               <th>IT Geb.</th><th>Webinar Gebucht ZP</th>
               <th>IT</th><th>Webinar Zeitpunkt</th>
               <th>EG geb.</th><th>EG Gebucht Zeitpunkt</th>
               <th>EG gef.</th><th>EG Geführt Zeitpunkt</th>
               <th>SG geb.</th> 
               <th>SG gef.</th><th>SG Geführt Zeitpunkt</th>
               <th>Starter</th><th>Starter Gebucht Zeitpunkt</th>
               <th>C0</th><th>C1</th><th>C2</th><th>C3</th><th>C4</th><th>C5</th>
               <th>Coaching Kunde Zeitpunkt</th>
               <th>Aktion</th>
               <th>Upload-Stufe</th>
             </tr>
           </thead>
           <tbody>
            {contacts.length > 0 ? (
              contacts.map(contact => (
                <tr key={contact.id}>
                  <td>{contact.email || "N/A"}</td>
                  <td>
                    <span className={`status-label status-${contact.status}`}>
                      {contact.status}
                    </span>
                  </td>
                  <td>{contact.vorname || "N/A"}</td>
                  <td><input type="text" value={contact.nachname || ""} onChange={(e) => handleNameChange(contact.id, e.target.value)} className="name-input"/></td>
                  <td>{formatDateTimeForDisplay(contact.erstellungsdatum)}</td>
                  <td>{formatDateTimeForDisplay(contact.gesendet_zeitpunkt)}</td>
                  
                  {/* NEUE ZELLEN */}
                  <td><input type="checkbox" checked={!!contact.it_gebucht} onChange={() => handleCheckboxChange(contact.id, "it_gebucht", contact.it_gebucht)} title="IT Gebucht"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.webinar_gebucht_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "webinar_gebucht_zeitpunkt", e.target.value)} /></td>

                  <td><input type="checkbox" checked={!!contact.it_besucht} onChange={() => handleCheckboxChange(contact.id, "it_besucht", contact.it_besucht)} title="IT besucht"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.webinar_engagiert_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "webinar_engagiert_zeitpunkt", e.target.value)} /></td>
                  <td><input type="checkbox" checked={!!contact.eg_gebucht} onChange={() => handleCheckboxChange(contact.id, "eg_gebucht", contact.eg_gebucht)} title="EG gebucht"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.eg_gebucht_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "eg_gebucht_zeitpunkt", e.target.value)} /></td>
                  <td><input type="checkbox" checked={!!contact.eg_gefuehrt} onChange={() => handleCheckboxChange(contact.id, "eg_gefuehrt", contact.eg_gefuehrt)} title="EG geführt"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.eg_gefuehrt_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "eg_gefuehrt_zeitpunkt", e.target.value)} /></td>
                  <td><input type="checkbox" checked={!!contact.sg_gebucht} onChange={() => handleCheckboxChange(contact.id, "sg_gebucht", contact.sg_gebucht)} title="SG gebucht"/></td>
                  <td><input type="checkbox" checked={!!contact.sg_gefuehrt} onChange={() => handleCheckboxChange(contact.id, "sg_gefuehrt", contact.sg_gefuehrt)} title="SG geführt"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.sg_gefuehrt_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "sg_gefuehrt_zeitpunkt", e.target.value)} /></td>
                  <td><input type="checkbox" checked={!!contact.coaching_starter_gebucht} onChange={() => handleCheckboxChange(contact.id, "coaching_starter_gebucht", contact.coaching_starter_gebucht)} title="Starter gebucht"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.coaching_starter_gebucht_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "coaching_starter_gebucht_zeitpunkt", e.target.value)} /></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c0} onChange={() => handleCheckboxChange(contact.id, "coaching_c0", contact.coaching_c0)} title="C0"/></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c1} onChange={() => handleCheckboxChange(contact.id, "coaching_c1", contact.coaching_c1)} title="C1"/></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c2} onChange={() => handleCheckboxChange(contact.id, "coaching_c2", contact.coaching_c2)} title="C2"/></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c3} onChange={() => handleCheckboxChange(contact.id, "coaching_c3", contact.coaching_c3)} title="C3"/></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c4} onChange={() => handleCheckboxChange(contact.id, "coaching_c4", contact.coaching_c4)} title="C4"/></td>
                  <td><input type="checkbox" checked={!!contact.coaching_c5} onChange={() => handleCheckboxChange(contact.id, "coaching_c5", contact.coaching_c5)} title="C5"/></td>
                  <td><input type="datetime-local" className="timestamp-input" value={formatDateTimeForInput(contact.coaching_kunde_zeitpunkt)} onChange={(e) => handleTimestampChange(contact.id, "coaching_kunde_zeitpunkt", e.target.value)} /></td>
                  <td><button onClick={() => handleGadsUpload(contact)} className="gads-upload-button" disabled={!selectedStage[contact.id]} title="Ausgewählte Stufe an Google Ads senden">Senden</button></td>
                  <td><select value={selectedStage[contact.id] || ""} onChange={(e) => handleStageSelectChange(contact.id, e.target.value)} className="stage-select">{funnelStages.map(stage => (<option key={stage.value} value={stage.value}>{stage.label}</option>))}</select></td>
                </tr>
              ))
            ) : (
               <tr><td colSpan={numberOfColumns}>Keine Kontakte gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <BackButton to="/admin" />
    </div>
  );
}
